import React, { useEffect } from 'react';
import { isIOS, osVersion } from 'react-device-detect';
import styled from 'styled-components';
import { useHistory } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { themeColor } from '../Theming';
import { Path } from '../Routing';
import TapHereAnimated from './Components/TapHereAnimated';
import Header from './Components/Header';
import { eonImages } from '../Theming/images';
import { usePageViewTracking, useQuery } from '../Common/Hooks';
import {
  ANALYTICS_ACTION_LABELS,
  ANALYTICS_PAGE_NAMES,
  trackActivationActionEvent,
} from '../Common/Analytics/utils';
import { getAnalytics, logEvent } from 'firebase/analytics';
import app from '../Firebase';

const Container = styled.div`
  max-width: 600px;
  margin: 0 auto;
`;

const Title = styled.h2<{ $fontSize?: string }>`
  color: ${themeColor('white')};
  font-family: Helvetica;
  text-align: center;
  padding-inline: 10vw;
  font-weight: 400;
  font-size: ${({ $fontSize }) => ($fontSize ? $fontSize : '24px')};
`;

const InfoContainer = styled.div`
  padding-top: 5vh;
`;

const InfoTitle = styled.h3`
  text-align: center;
  font-weight: 700;
  font-family: Helvetica;
`;

const ActivationStepsContainer = styled.div`
  flex-direction: row;
  display: flex;
  height: 30vh;
  justify-content: center;
  width: 100%;

  & > img {
    height: 100%;
  }

  & > ol {
    padding: 1.8vh 0 2vh 10px;
    margin: 0;
    list-style-type: none;
    color: ${themeColor('navyBlueText')};
    font-size: 16px;
    font-weight: 400;
    display: flex;
    flex-direction: column;
    justify-content: space-between;

    & > li {
      line-height: 1.5;
      font-family: Helvetica;
      font-weight: 700;
    }
  }
`;

interface Props {
  sliide?: boolean;
}

interface Message {
  data: string & { type: string; givenPermission?: boolean };
}

const EonPluginActivateMultiStep: React.FC<Props> = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const iOS18 = isIOS && parseInt(osVersion) >= 18;
  const infoItem = ['tapToolIcon', 'tapManageExtensions', 'toggleAppName'];
  const { safari_settings_steps, safari_settings_steps_ios_18 } = eonImages;
  const query = useQuery();
  const { activation_instructions_page } = ANALYTICS_PAGE_NAMES;
  const { activation_permissions } = ANALYTICS_ACTION_LABELS;
  usePageViewTracking(activation_instructions_page, 'Eon');

  useEffect(() => {
    const initialHeight = window.innerHeight;
    const analytics = getAnalytics(app);

    const interval = setInterval(() => {
      const currentHeight = window.innerHeight;

      if (currentHeight < initialHeight) {
        history.push({
          pathname: Path.EonPluginActivationAlwaysAllow,
          search: location.search,
        });
      }
    }, 1500);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const messageHandler = (message: Message) => {
      if (message && message.data === '') {
        window.location.reload();
      } else if (message) {
        switch (message.data?.type) {
          case 'PluginEnabled': {
            trackActivationActionEvent(
              query,
              'Eon',
              activation_permissions,
              activation_instructions_page,
              {
                permissions: message.data?.givenPermission ? 'Full' : 'Partial',
              }
            );
            logEvent(analytics, 'activation_permissions', {
              permissions: message.data?.givenPermission ? 'Full' : 'Partial',
              partner: 'Eon',
            });
            if (message.data?.givenPermission) {
              history.push({
                pathname: Path.EonPluginActivationStartShopping,
                search: location.search,
              });
            } else {
              history.push({
                pathname: Path.EonPluginActivationAlwaysAllow,
                search: location.search,
              });
            }
            break;
          }
        }
      }
    };

    window.addEventListener('message', messageHandler);

    return () => {
      clearInterval(interval);
      window.removeEventListener('message', messageHandler);
    };
  }, [history]);

  return (
    <Container>
      <Header
        height="30vh"
        titleContent={
          <Title $fontSize={window.screen.width > 380 ? '24px' : '20px'}>
            <Trans t={t} i18nKey="eon.activateExtension">
              {t('eon.activateExtension')}
            </Trans>
          </Title>
        }
      />
      <TapHereAnimated />
      <InfoContainer>
        <InfoTitle>{t('eon.whatYouNeedToDo')}</InfoTitle>
        <ActivationStepsContainer>
          <img
            src={
              iOS18
                ? safari_settings_steps_ios_18.default
                : safari_settings_steps.default
            }
            alt="safari activation settings steps"
          />
          <ol>
            {infoItem.map((item, index) => (
              <li key={index}>{t(`eon.${item}`)}</li>
            ))}
          </ol>
        </ActivationStepsContainer>
      </InfoContainer>
    </Container>
  );
};

export default EonPluginActivateMultiStep;
